import { CampaignClass, CampaignLabelMap, ECampaignClass } from '@feathr/blackbox';
import type { TCampaignRecord } from '@feathr/extender/hooks';

export interface IAccessConditions {
  showGoogleAds: boolean;
  showDripCampaigns: boolean;
}

export interface ITypeOption {
  id: CampaignClass | 'monetization';
  name?: string;
}

interface IGetOptionsArgs {
  permissions: TCampaignRecord;
  conditions: IAccessConditions;
  mailrail: boolean;
}

// Pinpoint and Mailrail are two backends for email campaigns. An account is on one or the other,
// gated by the `mailrail` feature flag. Without this gate the table filter shows both label sets
// and the inactive-backend options match nothing. Mirrors the create wizard's mutual exclusion.
const PINPOINT_EMAIL_CLASSES: CampaignClass[] = [
  CampaignClass.PinpointEmail,
  CampaignClass.SmartPinpointEmail,
  CampaignClass.AutoPinpointEmail,
  CampaignClass.Drip,
];
const MAILRAIL_EMAIL_CLASSES: CampaignClass[] = [
  CampaignClass.MailrailSingleSend,
  CampaignClass.MailrailAutoSend,
  CampaignClass.MailrailDrip,
];

export function getOptions({ permissions, conditions, mailrail }: IGetOptionsArgs): ITypeOption[] {
  const hiddenByMailrailFlag = mailrail ? PINPOINT_EMAIL_CLASSES : MAILRAIL_EMAIL_CLASSES;
  return (
    [
      ...Object.keys(ECampaignClass)
        .map((friendlyKey): ITypeOption | undefined => {
          const campaignKey = friendlyKey as keyof typeof ECampaignClass;
          const permission = permissions[campaignKey] as boolean | number;
          const realClassName = ECampaignClass[campaignKey] as unknown as CampaignClass;

          if (hiddenByMailrailFlag.includes(realClassName)) {
            return undefined;
          }

          // Special conditions for Google Ads and Drip campaigns
          if (friendlyKey === 'googleAdsSmart') {
            if (conditions.showGoogleAds && permission) {
              return {
                id: realClassName,
                name: CampaignLabelMap.get(realClassName),
              };
            }
            return undefined;
          }
          if (friendlyKey === 'drip') {
            if (conditions.showDripCampaigns && permission) {
              return {
                id: realClassName,
                name: CampaignLabelMap.get(realClassName),
              };
            }
            return undefined;
          }

          // Only return campaign types that are permitted
          // false = disabled, 0 = no quota, other falsy values = disabled
          // true, Infinity, or positive numbers = enabled
          if (permission && permission !== 0) {
            return {
              id: realClassName,
              name: CampaignLabelMap.get(realClassName),
            };
          }
          return undefined;
        })
        .filter((option): option is ITypeOption => option !== undefined),
      // Add monetization option if enabled
      ...(permissions.monetization ? [{ id: 'monetization' as const, name: 'Monetization' }] : []),
    ]
      // Alphabetize options to make searching easier
      .sort((a, b) => {
        if (!a.name) {
          return 1;
        }
        if (!b.name) {
          return -1;
        }
        return a.name.localeCompare(b.name);
      })
  );
}
